import { Box, Divider, Tab, Tabs as BaseTabs, Typography } from "@mui/material";
import Button from "components/atoms/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Dynamics has more top-level sections than fit comfortably in one tab row, and
 * five of them are lookup lists that belong together. Instead of a second tab
 * row, the bar swaps between two states in place:
 *
 *   collapsed  [Bookings Time] [Cross Country] ... [Selections >] ... [Markup]
 *   expanded   < General / Selections | [Movement Type] [Platform Type] ...
 *
 * The expanded strip sits on a tinted background and names the section you are
 * inside, so it always reads as one level down rather than as a second,
 * competing tab bar.
 */

/** Label for the tab group that stands in for every non-Selections section. */
export const GENERAL_LABEL = "General";

const STRIP_BG = "#f2f3f7";

// The two rows swap rather than coexist, so this is an enter-only animation:
// the incoming row fades and slides a few px toward its resting place. Sliding
// the two states in opposite directions makes expand/collapse feel reversible.
// Kept small on purpose — a full-width slide of the whole bar read as too abrupt.
const rowEnterSx = (expanded: boolean) => ({
    animation: "dynamicsRowIn .22s ease-out",
    "@keyframes dynamicsRowIn": {
        from: { opacity: 0, transform: `translateX(${expanded ? -12 : 12}px)` },
        to: { opacity: 1, transform: "translateX(0)" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

// Deliberately plain: the panel's own tab style is a clean underline with no
// background fill, and the expanded state is distinguished by the strip behind
// it, not by painting each tab.
const tabSx = {
    minHeight: 44,
    fontSize: 14,
    textTransform: "none" as const,
    color: "text.secondary",
    "&.Mui-selected": { color: "primary.main", fontWeight: 600 },
};

const tabsSx = {
    minHeight: 44,
    "& .MuiButtonBase-root": { padding: "8px 12px", minWidth: "unset" },
};

export interface DynamicsTabBarProps {
    /** Labels of the top-level sections, in order. */
    parentLabels: string[];
    /** Index into `parentLabels` of the section that owns the children. */
    expandableIndex: number;
    /** Labels of the child lists shown while expanded. */
    childLabels: string[];
    /** Active top-level section. */
    tab: number;
    /** Active child list. */
    child: number;
    expanded: boolean;
    onTabChange: (index: number) => void;
    onChildChange: (index: number) => void;
    /** Open the lookup lists (also makes the expandable section the active tab). */
    onExpand: () => void;
    /** Fold the lookup lists away and return to the default section. */
    onCollapse: () => void;
}

export default function DynamicsTabBar({
    parentLabels,
    expandableIndex,
    childLabels,
    tab,
    child,
    expanded,
    onTabChange,
    onChildChange,
    onExpand,
    onCollapse,
}: DynamicsTabBarProps) {
    const expandedRow = (
        <Box
            sx={{
                gap: 1.5,
                px: 1.5,
                display: "flex",
                alignItems: "stretch",
                borderBottom: 1,
                borderColor: "divider",
                backgroundColor: STRIP_BG,
                borderRadius: "8px 8px 0 0",
            }}
        >
            {/* Breadcrumb: where you are. Grouped together and closed off by the
                divider, so it reads as one unit and the tabs after it are the
                only tab-like things in the row. */}
            <Box sx={{ gap: 0.75, display: "flex", alignItems: "center", flexShrink: 0 }}>
                <Button
                    size="small"
                    variant="text"
                    onClick={onCollapse}
                    startIcon={<ChevronLeftIcon fontSize="small" />}
                    sx={{ textTransform: "none", color: "text.secondary" }}
                >
                    {GENERAL_LABEL}
                </Button>

                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                    /
                </Typography>

                {/* Deliberately NOT primary-coloured: in this panel purple means
                    "selected tab", so a purple label here competed with the
                    actually-selected child tab beside it. */}
                <Typography
                    variant="body2"
                    sx={{
                        pr: 0.5,
                        fontWeight: 600,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                    }}
                >
                    Selections
                </Typography>

                <Divider orientation="vertical" flexItem sx={{ my: 1.25 }} />
            </Box>

            <BaseTabs
                value={child}
                variant="scrollable"
                aria-label="Selection lists"
                sx={tabsSx}
                onChange={(_e, next) => onChildChange(next)}
            >
                {childLabels.map((label) => (
                    <Tab key={label} sx={tabSx} label={label} />
                ))}
            </BaseTabs>
        </Box>
    );

    const collapsedRow = (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <BaseTabs
                value={tab}
                variant="scrollable"
                aria-label="Dynamics sections"
                sx={tabsSx}
                onChange={(_e, next) =>
                    next === expandableIndex ? onExpand() : onTabChange(next)
                }
            >
                {parentLabels.map((label, index) =>
                    index === expandableIndex ? (
                        <Tab
                            key={label}
                            sx={tabSx}
                            label={label}
                            iconPosition="end"
                            icon={<ChevronRightIcon fontSize="small" />}
                            // MUI only fires onChange for a tab that is not already
                            // selected, so the expand toggle needs its own onClick.
                            onClick={onExpand}
                        />
                    ) : (
                        <Tab key={label} sx={tabSx} label={label} />
                    )
                )}
            </BaseTabs>
        </Box>
    );

    return (
        // Keyed so React remounts on the swap, which replays the enter animation.
        <Box key={expanded ? "expanded" : "collapsed"} sx={rowEnterSx(expanded)}>
            {expanded ? expandedRow : collapsedRow}
        </Box>
    );
}
